import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Plus, Send, Loader2, CheckCircle2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ClientShell, useClientUser } from "@/pages/client/ClientShell";

const inputCls = "w-full border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-[#6D28D9]/60";
const STATUS = { open: "border-amber-400 text-amber-700 bg-amber-50", answered: "border-emerald-600/40 text-emerald-700 bg-emerald-50", closed: "border-slate-300 text-slate-500 bg-white" };
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const TicketThread = ({ ticket, onReply, onClose, replyLabel = "Reply", canClose = true, testPrefix = "ticket" }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try { await onReply(text.trim()); setText(""); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`${testPrefix}-thread-${ticket.id}`} className="border-t border-slate-200 bg-[#F6F4FD]/60 px-5 py-5">
      <div className="space-y-3">
        {ticket.messages.map((m) => (
          <div key={m.id} data-testid={`${testPrefix}-message-${m.by}`} className={`flex ${m.by === "desk" ? "justify-start" : "justify-end"}`}>
            <div className={`max-w-[85%] px-4 py-3 text-sm ${m.by === "desk" ? "border border-[#6D28D9]/30 bg-white text-slate-800" : "bg-[#6D28D9] text-white"}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              <p className={`mt-1.5 font-mono2 text-[9px] uppercase tracking-[0.15em] ${m.by === "desk" ? "text-[#6D28D9]" : "text-white/70"}`}>{m.author} · {fmt(m.at)}</p>
            </div>
          </div>
        ))}
      </div>
      {ticket.status !== "closed" ? (
        <form onSubmit={send} className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
          <textarea data-testid={`${testPrefix}-reply-input-${ticket.id}`} rows={2} value={text} onChange={(e) => setText(e.target.value)} maxLength={3000} placeholder="Write a reply…" className={`${inputCls} flex-1 resize-none`} />
          <div className="flex gap-2">
            <button data-testid={`${testPrefix}-reply-send-${ticket.id}`} type="submit" disabled={busy || !text.trim()} className="flex items-center gap-2 bg-[#6D28D9] px-4 py-3 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#5B21B6] disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} {replyLabel}</button>
            {canClose && <button data-testid={`${testPrefix}-close-${ticket.id}`} type="button" onClick={onClose} className="flex items-center gap-1.5 border border-slate-200 px-4 py-3 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-600 hover:border-emerald-500 hover:text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Close</button>}
          </div>
        </form>
      ) : <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-400">This ticket is closed.</p>}
    </div>
  );
};

export const TicketRow = ({ t, open, onToggle, children }) => (
  <div data-testid={`ticket-${t.id}`} className="border border-slate-200 bg-white">
    <button data-testid={`ticket-toggle-${t.id}`} onClick={onToggle} className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left">
      <div className="min-w-0">
        <p className="font-display text-base font-semibold text-slate-900">{t.subject}</p>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500"><span className="text-[#6D28D9]">{t.ref}</span> · {t.category} · {t.messages.length} message{t.messages.length > 1 ? "s" : ""} · {fmt(t.updated_at)}</p>
      </div>
      <span data-testid={`ticket-status-${t.id}`} className={`border px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.15em] ${STATUS[t.status] || STATUS.closed}`}>{t.status}</span>
    </button>
    {open && children}
  </div>
);

export default function ClientSupport() {
  const user = useClientUser();
  const [data, setData] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "Orders & Payments", message: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/client/tickets").then((r) => setData(r.data)).catch(() => setData({ categories: [], items: [] }));
  useEffect(() => { if (user) load(); }, [user]);
  if (!user) return null;

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/client/tickets", form);
      toast.success(`Ticket ${res.data.ref} raised — the desk will reply here.`);
      setShowNew(false); setForm({ subject: "", category: "Orders & Payments", message: "" });
      await load(); setOpen(res.data.id);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const reply = (id) => async (text) => { await api.post(`/client/tickets/${id}/messages`, { text }); await load(); };
  const close = (id) => async () => { try { await api.patch(`/client/tickets/${id}/close`); toast.success("Ticket closed."); await load(); } catch (err) { toast.error(formatApiError(err)); } };

  return (
    <ClientShell user={user} title="Support">
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Raise a ticket and the desk replies here. Urgent? WhatsApp +91 81696 47280.</p>
        <button data-testid="ticket-new-button" onClick={() => setShowNew(!showNew)} className="flex items-center gap-2 bg-[#6D28D9] px-5 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#5B21B6]">{showNew ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {showNew ? "Cancel" : "New ticket"}</button>
      </div>

      {showNew && (
        <form data-testid="ticket-form" onSubmit={create} className="mt-6 grid gap-4 border border-slate-200 bg-[#F6F4FD]/70 p-6 md:grid-cols-2">
          <label className="block"><span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Subject</span><input data-testid="ticket-subject-input" required minLength={3} maxLength={120} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={`mt-1.5 ${inputCls}`} /></label>
          <label className="block"><span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Category</span>
            <select data-testid="ticket-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`mt-1.5 ${inputCls}`}>{(data?.categories || ["Other"]).map((c) => <option key={c}>{c}</option>)}</select></label>
          <label className="block md:col-span-2"><span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-500">Message</span><textarea data-testid="ticket-message-input" required minLength={5} maxLength={3000} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`mt-1.5 ${inputCls}`} placeholder="Describe the issue — include an order reference if relevant." /></label>
          <div className="md:col-span-2 flex justify-end"><button data-testid="ticket-submit-button" type="submit" disabled={busy} className="flex items-center gap-2 bg-[#6D28D9] px-6 py-3 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-[#5B21B6] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Raise ticket</button></div>
        </form>
      )}

      {!data && <p className="mt-8 font-mono2 text-xs uppercase tracking-[0.25em] text-slate-500">Loading tickets…</p>}
      {data?.items.length === 0 && !showNew && (
        <div data-testid="tickets-empty" className="mt-8 flex flex-col items-center border border-dashed border-slate-300 bg-[#F6F4FD]/60 px-6 py-14 text-center"><LifeBuoy className="h-6 w-6 text-[#6D28D9]" /><p className="font-display mt-4 text-lg font-semibold">No tickets yet</p><p className="mt-1 text-sm text-slate-500">Questions about an order, KYC or your account? Raise a ticket and we'll reply here.</p></div>
      )}
      <div data-testid="tickets-list" className="mt-6 space-y-3">
        {data?.items.map((t) => (
          <TicketRow key={t.id} t={t} open={open === t.id} onToggle={() => setOpen(open === t.id ? null : t.id)}>
            <TicketThread ticket={t} onReply={reply(t.id)} onClose={close(t.id)} />
          </TicketRow>
        ))}
      </div>
    </ClientShell>
  );
}
